import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import type { Vendor } from '@/types/api';
import type { ExistingItemRef, ImportPreview, SkipReason } from '@/types/procurementImport';
import { buildConfirmPlan, combinedWith, existingRowsFor, getRowProblems, rowGroup, type ConfirmPlan, type ImportRowState, type RowGroup } from './confirmPlan';
import { ImportRowEditor } from './ImportRowEditor';
import { SelectBox } from './SelectBox';

const GROUP_TITLES: Record<RowGroup, string> = {
  attention: 'Needs a quick look',
  merged: 'Clubbed from several days',
  ready: 'Ready to add',
  kitchen: 'In the kitchen already',
  'left-out': 'Left out',
};
const GROUP_ORDER: RowGroup[] = ['attention', 'merged', 'ready', 'kitchen', 'left-out'];
const SKIP_LABELS: Record<SkipReason, string> = {
  note: 'Message, not an item',
  'spice-block': 'Weekly spice list (covered by staples)',
  'in-kitchen': 'Marked as in the kitchen',
  empty: 'No ingredient name',
};

export function formatLongDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

export interface ConfirmMeta {
  /** Rows left unticked */
  excludedCount: number;
}

interface ReviewStepProps {
  preview: ImportPreview;
  rowStates: ImportRowState[];
  /** Every item on this week's list, excluded ones included */
  existingItems: ExistingItemRef[];
  /** Active vendors for each row's Vendor select */
  vendors?: Vendor[];
  onRowChange: (key: string, patch: Partial<ImportRowState>) => void;
  onTabChange: (tab: string) => void;
  onConfirm: (plan: ConfirmPlan, meta: ConfirmMeta) => void;
  confirming: boolean;
  /** Tab being re-read, while a read is in flight */
  readingTab?: string | null;
}

export function ReviewStep({ preview, rowStates, existingItems, vendors = [], onRowChange, onTabChange, onConfirm, confirming, readingTab = null }: ReviewStepProps) {
  const [showSkipped, setShowSkipped] = useState(false);
  const tabId = useId();
  // Fresh per preview so a re-read never drops a new pick as stale.
  const pickSeqs = useMemo(() => new Map<string, number>(), [preview]);

  const plan = useMemo(() => buildConfirmPlan(rowStates, existingItems), [existingItems, rowStates]);
  // A plan problem with no row problem (e.g. a combined total) still shows on its row, so Confirm is never disabled silently.
  const problemsByKey = useMemo(() => {
    const byKey = new Map(rowStates.map((s) => [s.key, getRowProblems(s, rowStates, existingItems)]));
    for (const problem of plan.problems) {
      const current = byKey.get(problem.key);
      if (current && current.length === 0) byKey.set(problem.key, [problem.message]);
    }
    return byKey;
  }, [existingItems, plan.problems, rowStates]);
  // A row being edited stays in its group until focus leaves it, so typing never moves the field away.
  const [heldKey, setHeldKey] = useState<string | null>(null);
  const placement = useRef(new Map<string, RowGroup>());
  const grouped = useMemo(() => {
    const groups: Record<RowGroup, ImportRowState[]> = { attention: [], merged: [], ready: [], kitchen: [], 'left-out': [] };
    for (const state of rowStates) {
      const live = rowGroup(state, problemsByKey.get(state.key) ?? [], existingItems);
      const held = state.key === heldKey && state.included ? placement.current.get(state.key) : undefined;
      groups[held ?? live].push(state);
    }
    return groups;
  }, [existingItems, heldKey, problemsByKey, rowStates]);
  useEffect(() => {
    placement.current = new Map(GROUP_ORDER.flatMap((group) => grouped[group].map((state) => [state.key, group] as const)));
  }, [grouped]);
  const excludedCount = rowStates.filter((s) => !s.included).length;

  const datedTabs = preview.tabs.filter((t) => t.weekStart !== null);
  const attention = grouped.attention.length;
  const mismatch = preview.chosenTabWeekStart !== null && preview.chosenTabWeekStart !== preview.week.weekStartDate;
  const label = `Add ${plan.itemCount} item${plan.itemCount === 1 ? '' : 's'} to this week`;

  return (
    <div className="space-y-4">
      {datedTabs.length > 1 && (
        <div className="flex items-center gap-2 text-sm">
          <label htmlFor={tabId}>Tab</label>
          <SelectBox id={tabId} className="w-auto min-w-48" value={preview.chosenTab} disabled={readingTab !== null} onChange={(e) => onTabChange(e.target.value)}>
            {preview.tabs.map((t) => <option key={t.name} value={t.name}>{t.name}{t.weekStart ? ` — week of ${formatLongDate(t.weekStart)}` : ''}</option>)}
          </SelectBox>
        </div>
      )}

      {readingTab !== null && <p role="status" className="text-sm text-muted-foreground">Reading tab {readingTab}…</p>}

      {mismatch && (
        <p role="status" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          This tab is for the week of {formatLongDate(preview.chosenTabWeekStart!)}, and you're importing into {formatLongDate(preview.week.weekStartDate)}. Switch tabs above, or continue if that's intended.
        </p>
      )}

      {preview.warnings?.map((warning) => (
        <p key={warning} role="status" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">{warning}</p>
      ))}

      <p className="text-sm text-muted-foreground">
        {preview.summary.total} items. {attention} need a quick look, {preview.summary.merged} were clubbed from several days, {preview.summary.skipped} skipped.
      </p>

      <div data-testid="import-rows" aria-busy={readingTab !== null ? true : undefined} className={`space-y-4 ${readingTab !== null ? 'pointer-events-none opacity-60' : ''}`}>
      {GROUP_ORDER.map((group) => grouped[group].length > 0 && (
        <section key={group} className="space-y-2">
          <h3 data-testid="import-group-heading" className="text-sm font-medium">{GROUP_TITLES[group]}</h3>
          <div data-testid="import-column-header" aria-hidden="true" className="hidden grid-cols-[auto_minmax(0,1fr)_6rem_7rem_11rem] gap-x-3 px-[calc(0.75rem+1px)] text-xs text-muted-foreground md:grid">
            <span className="w-4" />
            <span>Ingredient</span>
            <span>Qty</span>
            <span>Unit</span>
            <span>Vendor</span>
          </div>
          {grouped[group].map((state) => (
            <div
              key={state.key}
              onFocus={() => setHeldKey(state.key)}
              onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setHeldKey((held) => (held === state.key ? null : held)); }}
            >
            <ImportRowEditor
              state={state}
              problems={problemsByKey.get(state.key) ?? []}
              existingRows={existingRowsFor(state, existingItems)}
              existingItems={existingItems}
              pickSeqs={pickSeqs}
              combinedWith={combinedWith(state, rowStates)}
              vendors={vendors}
              onChange={(patch) => {
                // Ticking or unticking moves the row at once; only edits inside a focused row wait for blur.
                if ('included' in patch) setHeldKey(null);
                onRowChange(state.key, patch);
              }}
            />
            </div>
          ))}
        </section>
      ))}
      </div>

      {preview.skipped.length > 0 && (
        <section className="space-y-2">
          <button type="button" className="text-sm underline underline-offset-4" onClick={() => setShowSkipped((v) => !v)}>
            Skipped rows ({preview.skipped.length})
          </button>
          {showSkipped && (
            <ul className="space-y-1 text-sm">
              {preview.skipped.map((s, i) => (
                <li key={`${s.cellRef}-${i}`} className="flex flex-wrap justify-between gap-2 rounded-md border px-3 py-1.5">
                  <span>{s.raw}</span>
                  <span className="flex gap-1 text-xs text-muted-foreground">
                    <span>{SKIP_LABELS[s.reason]}</span>
                    <span aria-hidden="true">·</span>
                    <span>{s.cellRef}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </section>
      )}

      <div data-testid="import-footer" className="sticky bottom-0 -mx-6 flex flex-wrap items-center justify-between gap-3 border-t bg-background px-6 pt-3">
        <p className="text-sm text-muted-foreground">
          {plan.itemCount} item{plan.itemCount === 1 ? '' : 's'} will be added, {excludedCount} left out
        </p>
        <Button onClick={() => onConfirm(plan, { excludedCount })} disabled={confirming || plan.itemCount === 0}>
          {confirming ? 'Adding…' : label}
        </Button>
      </div>
    </div>
  );
}
